import { totalScoreMinimumMessage, usClassroomCopy } from "../copy/usClassroomTerms";
import { TOOL_DISPLAY_NAMES } from "../tools/toolDisplayNames";

export const builderHeading = TOOL_DISPLAY_NAMES["question-paper"];
export const builderSubtitle =
  "Build structured, institutional-grade assessments with blueprint alignment";

export const topicPastePlaceholder = "Type here...";

export const uploadContentLinkLabel = "Import Syllabus / Notes";

export const previewDocumentContent = [
  "Unit 3: Ecosystems and Energy Flow. Students explain how producers, consumers, and decomposers interact within food webs and describe how energy transfers between trophic levels.",
  "Assessment focus: define key vocabulary, interpret simple food-web diagrams, and answer short constructed-response questions about human impacts on local ecosystems.",
];

export const subjectTopicHeading = "SUBJECT & TOPIC";
export const blueprintSourceHeading = "BLUEPRINT SOURCE";
export const sectionConfigHeading = "SECTION CONFIGURATION (OPTIONAL)";
export const createNewSectionConfigHeading = "4. SECTION CONFIGURATION (OPTIONAL)";
export const addNewSectionLabel = "ADD NEW SECTION";

export const MAX_QUESTION_PAPER_TOTAL_MARKS = 250;
export const MIN_QUESTION_PAPER_TOTAL_MARKS = 10;
export const MAX_EXAM_SECTIONS = 8;

export const totalMarksMinimumMessage = totalScoreMinimumMessage();

export function isQuestionPaperTotalMarksTooLow(totalMarks) {
  const value = Number(totalMarks);
  return !Number.isFinite(value) || value < MIN_QUESTION_PAPER_TOTAL_MARKS;
}

export function isOnlyTotalMarksValidationError(errors) {
  const fields = Object.keys(errors ?? {});
  return fields.length === 1 && fields[0] === "totalMarks";
}

export const totalMarksFromSectionsHint = usClassroomCopy(
  "Calculated automatically from section points — not editable. Maximum 250 points.",
  "Calculated automatically from section marks — not editable. Maximum 250 marks.",
);
export const totalMarksFromQuestionTypesHint = usClassroomCopy(
  "Calculated automatically from question types — not editable. Maximum 250 points.",
  "Calculated automatically from question types — not editable. Maximum 250 marks.",
);

export const sectionTypeOptions = [
  { id: "mcq", label: "MCQ" },
  { id: "shortAnswer", label: "Short Answer" },
  { id: "longAnswer", label: "Long Answer" },
  { id: "fillBlanks", label: "Fill in the Blank" },
  { id: "trueFalse", label: "True / False" },
];

let examSectionIdCounter = 0;

export function createExamSection(overrides = {}) {
  examSectionIdCounter += 1;
  return normalizeExamSectionCounts({
    id: `exam-section-${examSectionIdCounter}`,
    name: "Section A",
    type: "mcq",
    questionCount: 10,
    questionsToAttempt: 10,
    marksPerQuestion: 1,
    ...overrides,
  });
}

export function normalizeExamSectionCounts(section) {
  const questionCount = parseSectionCount(section.questionCount, { min: 1 });
  const questionsToAttempt = parseSectionCount(section.questionsToAttempt, { min: 1 });
  const marksPerQuestion = parseSectionCount(section.marksPerQuestion, { min: 1 });

  if (questionCount == null) {
    return {
      ...section,
      questionCount: null,
      questionsToAttempt,
      marksPerQuestion,
    };
  }

  const cappedAttempt =
    questionsToAttempt == null
      ? null
      : Math.min(questionCount, questionsToAttempt);

  return {
    ...section,
    questionCount,
    questionsToAttempt: cappedAttempt,
    marksPerQuestion,
  };
}

function parseSectionCount(value, { min } = {}) {
  if (value == null || value === "") {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return min != null ? Math.max(min, parsed) : parsed;
}

export function normalizeExamSections(sections = []) {
  return sections
    .slice(0, MAX_EXAM_SECTIONS)
    .map((section) => normalizeExamSectionCounts(section));
}

export function capQuestionPaperTotalMarks(totalMarks) {
  const value = Number(totalMarks);
  if (!Number.isFinite(value)) {
    return null;
  }
  return Math.min(MAX_QUESTION_PAPER_TOTAL_MARKS, Math.max(0, value));
}

export const defaultExamSections = [
  createExamSection({ name: "Section A", type: "mcq" }),
  createExamSection({
    name: "Section B",
    type: "shortAnswer",
    marksPerQuestion: 4,
  }),
  createExamSection({
    name: "Section C",
    type: "longAnswer",
    questionCount: 5,
    questionsToAttempt: 5,
    marksPerQuestion: 6,
  }),
];

export function formatExamSectionIndex(index) {
  return `#${String(index + 1).padStart(2, "0")}`;
}

export function buildExamSectionsContent(sections = []) {
  if (!sections.length) {
    return "";
  }
  return [
    "Exam section configuration:",
    ...sections.map(
      (section, index) =>
        `- ${formatExamSectionIndex(index)} ${section.name}: type=${section.type}, questions=${section.questionCount}, attempt=${section.questionsToAttempt}, marksPerQuestion=${section.marksPerQuestion}`,
    ),
  ].join("\n");
}

export function calculateExamSectionsTotalMarks(sections = []) {
  return sections.reduce((total, section) => {
    const questionsToAttempt = Number(section.questionsToAttempt) || 0;
    const marksPerQuestion = Number(section.marksPerQuestion) || 0;
    return total + questionsToAttempt * marksPerQuestion;
  }, 0);
}

const QUESTION_TYPE_SECTION_TYPES = {
  mcq: ["mcq"],
  shortAnswer: ["shortAnswer"],
  trueFalse: ["trueFalse"],
  longAnswer: ["longAnswer"],
  caseBased: ["shortAnswer"],
  fillBlanks: ["fillBlanks"],
  assertionReason: ["mcq"],
};

export function resolveAllowedSectionTypes(questionTypeIds = []) {
  const allowed = new Set();
  for (const typeId of questionTypeIds) {
    for (const sectionType of QUESTION_TYPE_SECTION_TYPES[typeId] ?? []) {
      allowed.add(sectionType);
    }
  }
  return allowed;
}

export function filterExamSectionsForQuestionTypes(examSections = [], questionTypeIds = []) {
  const allowed = resolveAllowedSectionTypes(questionTypeIds);
  if (!allowed.size) {
    return [];
  }
  return examSections.filter((section) => allowed.has(section.type));
}

const SECTION_TYPE_TO_QUESTION_TYPE_ID = {
  mcq: "mcq",
  shortAnswer: "shortAnswer",
  longAnswer: "longAnswer",
  fillBlanks: "fillBlanks",
  trueFalse: "trueFalse",
};

const DERIVED_QUESTION_TYPE_ORDER = [
  "mcq",
  "shortAnswer",
  "trueFalse",
  "longAnswer",
  "fillBlanks",
  "caseBased",
  "assertionReason",
];

export function deriveQuestionTypesFromExamSections(examSections = []) {
  const seen = new Set();
  for (const section of examSections) {
    const typeId = SECTION_TYPE_TO_QUESTION_TYPE_ID[section.type];
    if (typeId) {
      seen.add(typeId);
    }
  }
  return DERIVED_QUESTION_TYPE_ORDER.filter((typeId) => seen.has(typeId));
}

export function resolveQuestionTypesForGeneration(config) {
  if (config.enableSections && config.examSections?.length) {
    const derived = deriveQuestionTypesFromExamSections(config.examSections);
    if (derived.length > 0) {
      return derived;
    }
  }

  const configs = resolveQuestionTypeConfigs(config);
  const enabled = resolveEnabledQuestionTypeIds(configs);
  if (enabled.length > 0) {
    return enabled;
  }

  return config.questionTypes?.length ? config.questionTypes : ["mcq"];
}
export const syllabusHighlightsHeading = "SYLLABUS COVERAGE HIGHLIGHTS";
export const addSyllabusHighlightLabel = "Add syllabus coverage highlight";
export const enableSectionsLabel = "ENABLE SECTIONS";
export const enableTeacherSheetLabel = "ENABLE TEACHER SHEET MODE";
export const syllabusCoverageLabel = "Syllabus Coverage";
export const topicsMappedLabel = "Topics Mapped";
export const analyzingSyllabusLabel = "Analyzing syllabus…";

export const blueprintOptions = [
  { id: "none", label: "No Blueprint" },
  { id: "systemLibrary", label: "System Library" },
  { id: "upload", label: "Upload" },
  { id: "createNew", label: "Create New" },
];

export const examModes = [
  { id: "examAligned", label: "EXAM-ALIGNED MODE" },
];

export const examConfigHeading = "EXAM CONFIGURATION";
export const createNewExamConfigHeading = "3. EXAM CONFIGURATION";

export const learningObjectivesWeightageHeading = "LEARNING OBJECTIVES WEIGHTAGE";

export const learningObjectiveWeightageOptions = [
  {
    id: "recall",
    label: "Recall facts and basic concepts",
    defaultPercent: 20,
  },
  {
    id: "apply",
    label: "Apply knowledge in new situations",
    defaultPercent: 40,
  },
  {
    id: "analyze",
    label: "Analyze and connect ideas",
    defaultPercent: 40,
  },
];

export function buildCreateNewBlueprintContent(weightage) {
  const recall = weightage?.recall ?? 20;
  const apply = weightage?.apply ?? 40;
  const analyze = weightage?.analyze ?? 40;
  return [
    "Learning objectives weightage:",
    `- Recall facts and basic concepts: ${recall}%`,
    `- Apply knowledge in new situations: ${apply}%`,
    `- Analyze and connect ideas: ${analyze}%`,
  ].join("\n");
}

export const defaultLearningObjectivesWeightage = {
  recall: 20,
  apply: 40,
  analyze: 40,
};

const defaultExamYear = new Date().getFullYear();

export const examNamePlaceholder = `Mid-Term ${defaultExamYear}`;

export const examTypes = [
  { id: "final", label: "Final Examination" },
  { id: "midterm", label: "Mid-Term Examination" },
  { id: "unit", label: "Unit Test" },
  { id: "practice", label: "Practice Paper" },
];

export const questionTypesHeading = "ALLOWED QUESTION TYPES";

export const questionTypeOptions = [
  { id: "mcq", label: "MCQS" },
  { id: "shortAnswer", label: "SHORT ANSWER" },
  { id: "trueFalse", label: "TRUE / FALSE" },
  { id: "longAnswer", label: "LONG ANSWER" },
  { id: "caseBased", label: "CASE-BASED" },
  { id: "fillBlanks", label: "FILL IN THE BLANKS" },
  { id: "assertionReason", label: "ASSERTION & REASON" },
];

export const difficultyHeading = "DIFFICULTY DISTRIBUTION";

export const difficultyOptions = [
  { id: "balanced", label: "Balanced" },
  { id: "easyHeavy", label: "Easy Heavy" },
  { id: "hardHeavy", label: "Hard Heavy" },
];

export const questionTypeDifficultyOptions = [
  { id: "default", label: "Default" },
  ...difficultyOptions,
];

const DEFAULT_TYPE_COUNTS = {
  mcq: { questionCount: 4, marksPerQuestion: 1 },
  shortAnswer: { questionCount: 0, marksPerQuestion: 2 },
  trueFalse: { questionCount: 0, marksPerQuestion: 1 },
  longAnswer: { questionCount: 0, marksPerQuestion: 5 },
  caseBased: { questionCount: 0, marksPerQuestion: 4 },
  fillBlanks: { questionCount: 0, marksPerQuestion: 1 },
  assertionReason: { questionCount: 0, marksPerQuestion: 1 },
};

export function createQuestionTypeConfig(typeId, overrides = {}) {
  const defaults = DEFAULT_TYPE_COUNTS[typeId] ?? { questionCount: 0, marksPerQuestion: 1 };
  return normalizeQuestionTypeConfig({
    typeId,
    enabled: typeId === "mcq",
    questionCount: defaults.questionCount,
    marksPerQuestion: defaults.marksPerQuestion,
    difficultyDistribution: "default",
    ...overrides,
  });
}

export function createDefaultQuestionTypeConfigs() {
  return questionTypeOptions.map((option) => createQuestionTypeConfig(option.id));
}

export function normalizeQuestionTypeConfig(entry) {
  const questionCount = parseSectionCount(entry.questionCount, { min: 0 });
  const marksPerQuestion = parseSectionCount(entry.marksPerQuestion, { min: 1 });

  let resolvedQuestionCount = questionCount;
  if (questionCount != null) {
    resolvedQuestionCount = entry.enabled
      ? Math.max(1, questionCount)
      : Math.max(0, questionCount);
  }

  let resolvedMarksPerQuestion = marksPerQuestion;
  if (marksPerQuestion != null) {
    resolvedMarksPerQuestion = Math.max(1, marksPerQuestion);
  }

  return {
    typeId: entry.typeId,
    enabled: Boolean(entry.enabled),
    questionCount: resolvedQuestionCount,
    marksPerQuestion: resolvedMarksPerQuestion,
    difficultyDistribution: entry.difficultyDistribution || "default",
  };
}

export function normalizeQuestionTypeConfigs(configs = []) {
  const byTypeId = new Map(
    configs.map((entry) => [entry.typeId, normalizeQuestionTypeConfig(entry)]),
  );

  return questionTypeOptions.map((option) => {
    const existing = byTypeId.get(option.id);
    if (existing) {
      return normalizeQuestionTypeConfig(existing);
    }
    return createQuestionTypeConfig(option.id, { enabled: false, questionCount: 0 });
  });
}

export function resolveQuestionTypeConfigs(config) {
  if (config.questionTypeConfigs?.length) {
    return normalizeQuestionTypeConfigs(config.questionTypeConfigs);
  }

  return normalizeQuestionTypeConfigs(
    questionTypeOptions.map((option) =>
      createQuestionTypeConfig(option.id, {
        enabled: config.questionTypes?.includes(option.id) ?? option.id === "mcq",
        questionCount: config.questionTypes?.includes(option.id)
          ? (DEFAULT_TYPE_COUNTS[option.id]?.questionCount || 2)
          : 0,
      }),
    ),
  );
}

export function calculateQuestionTypeConfigsTotalMarks(configs = []) {
  return configs.reduce((total, entry) => {
    if (!entry.enabled || entry.questionCount <= 0) {
      return total;
    }
    return total + entry.questionCount * entry.marksPerQuestion;
  }, 0);
}

export function resolveEnabledQuestionTypeIds(configs = []) {
  return configs
    .filter((entry) => entry.enabled && entry.questionCount > 0)
    .map((entry) => entry.typeId);
}

export function buildQuestionTypeConfigsContent(configs = []) {
  const enabled = configs.filter((entry) => entry.enabled && entry.questionCount > 0);
  if (!enabled.length) {
    return "";
  }

  return [
    "Allowed question type configuration:",
    ...enabled.map((entry) => {
      const label =
        questionTypeOptions.find((option) => option.id === entry.typeId)?.label ??
        entry.typeId;
      const difficulty =
        questionTypeDifficultyOptions.find(
          (option) => option.id === entry.difficultyDistribution,
        )?.label ?? "Default";
      const scoreEach = usClassroomCopy(
        `${entry.marksPerQuestion} ${Number(entry.marksPerQuestion) === 1 ? "point" : "points"}`,
        `${entry.marksPerQuestion} mark(s)`,
      );
      return `- ${label}: ${entry.questionCount} questions, ${scoreEach} each, difficulty: ${difficulty}`;
    }),
  ].join("\n");
}

export const questionSetOptions = [1, 2, 3];

export const answerKeyOptions = [
  { id: "inline", label: "Inline" },
  { id: "separate", label: "Separate" },
  { id: "hidden", label: "Hidden" },
];

export const generatePaperLabel = usClassroomCopy(
  "Generate Assessment",
  "Generate Question Paper",
);
export const generatePaperHint = usClassroomCopy(
  "Creates a structured, exam-ready assessment",
  "Creates a structured, exam-ready paper",
);

export const generatingPaperMessage = usClassroomCopy(
  "Generating your assessment…",
  "Generating your question paper…",
);

export const defaultQuestionPaperConfig = {
  selectedTopicId: "",
  topicContent: "",
  blueprintSource: "systemLibrary",
  examMode: "examAligned",
  examName: `Mid-Term ${defaultExamYear}`,
  examType: "final",
  totalMarks: calculateQuestionTypeConfigsTotalMarks(createDefaultQuestionTypeConfigs()),
  durationMinutes: null,
  syllabusCoveragePercent: 0,
  topicsMapped: 0,
  syllabusHighlights: [],
  enableSections: false,
  examSections: [],
  questionTypes: ["mcq"],
  questionTypeConfigs: createDefaultQuestionTypeConfigs(),
  difficultyDistribution: "balanced",
  enableTeacherSheetMode: false,
  answerKeyMode: "inline",
  questionSets: 1,
  selectedBlueprintId: "",
  selectedBlueprintTitle: "",
  learningObjectivesWeightage: { ...defaultLearningObjectivesWeightage },
};
